/**
 * UnitEconomicsSection — M12 headline metrics + tier breakdown table.
 *
 * If the UnitEconomicsContext is available (user has customised inputs via the
 * admin editor), the section renders calculated values from the engine.
 * Otherwise it falls back to the static data from the pitch deck content file.
 */
import { motion } from "framer-motion";
import PitchSection from "../PitchSection";
import SectionLabel from "../SectionLabel";
import InfoTooltip from "../InfoTooltip";
import { useUnitEconomics } from "../../../contexts/UnitEconomicsContext";

export default function UnitEconomicsSection({ data }) {
  // Try to get live-calculated data; falls back to null if no provider
  let live = null;
  try { live = useUnitEconomics(); } catch { /* no provider — use static data */ }

  // When the context is available, use computed values; otherwise fall back to static props
  const hl    = live ? live.computed.headline : data.headline;
  const tiers = live ? live.computed.tiers     : data.tiers;
  const wArpu = live ? live.computed.weightedArpu : data.weightedArpu;
  const wGm   = live ? live.computed.weightedGm   : data.weightedGm;
  const wLtv  = live ? live.computed.weightedLtv  : data.weightedLtv;

  const metrics = [
    { label: "Subscribers", value: hl.subs },
    { label: "MRR", value: hl.mrr },
    { label: "Gross Margin", value: hl.grossMargin },
    { label: "CAC", value: hl.cac },
    { label: "Payback", value: hl.payback },
    { label: "LTV:CAC", value: hl.ltvCac },
    { label: "Net Monthly", value: hl.netMonthly },
    { label: "Cash Position", value: hl.cashPosition },
  ];

  return (
    <PitchSection id="unit-economics">
      <SectionLabel>{data.label}</SectionLabel>
      <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4 leading-tight flex items-center gap-2 flex-wrap">
        {data.title}
        {data.tooltip && <InfoTooltip>{data.tooltip}</InfoTooltip>}
      </h2>

      {/* Headline metrics grid */}
      <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-3 mb-10">
        {metrics.map((m, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.06 }}
            className="bg-[#0F1826]/60 border border-white/8 rounded-xl p-4 text-center"
          >
            <p className="text-2xl font-bold text-white">{m.value}</p>
            <p className="text-xs text-gray-400 mt-1">{m.label}</p>
          </motion.div>
        ))}
      </div>

      {/* Tier breakdown table */}
      <motion.div
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        className="overflow-x-auto rounded-2xl border border-white/8"
      >
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-[#0F1826]/80 text-left">
              {["Tier", "Price", "Infra", "GM$/mo", "Retention", "LTV (GM$)", "LTV:CAC"].map((h) => (
                <th key={h} className="px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider whitespace-nowrap">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {tiers.map((t, i) => (
              <tr
                key={i}
                className="border-t border-white/5 hover:bg-white/[0.02] transition-colors"
              >
                <td className="px-4 py-3 text-white font-medium">{t.tier}</td>
                <td className="px-4 py-3 text-gray-300">{t.price}</td>
                <td className="px-4 py-3 text-gray-400">{t.infra}</td>
                <td className="px-4 py-3 text-[#4FA9A1] font-medium">{t.gm}</td>
                <td className="px-4 py-3 text-gray-300">{t.retention}</td>
                <td className="px-4 py-3 text-[#60A5FA] font-medium">{t.ltv}</td>
                <td className="px-4 py-3 text-white font-bold">{t.ltvCac}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </motion.div>

      {/* Weighted averages */}
      <div className="mt-6 flex flex-wrap gap-6 text-base">
        <div>
          <span className="text-gray-400">Weighted ARPU:</span>{" "}
          <span className="text-white font-medium">{wArpu}</span>
        </div>
        <div>
          <span className="text-gray-400">Weighted GM:</span>{" "}
          <span className="text-white font-medium">{wGm}</span>
        </div>
        <div>
          <span className="text-gray-400">Weighted LTV:</span>{" "}
          <span className="text-white font-bold">{wLtv}</span>
        </div>
      </div>
    </PitchSection>
  );
}
